import { useEffect, useState } from "react";
import { useResource } from "@/hooks/useResource";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LoadingState, ErrorState } from "@/components/common/States";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Users, Pencil, Save, X, Loader2, ShieldAlert, CheckCircle2, Send } from "lucide-react";

// Reports & Email settings.
// The shared backend derives report/requirement email targets from GET /reports/email-config
// (fromName + replyTo + recipients). replyTo == Company Master `email`, which is the single
// writable lever (PUT /settings/company). Additional recipients are the active admin/management
// users, managed under Users & Roles. Nothing here is invented — it reflects the live config.
export default function EmailRecipients() {
  const { role } = useAuth();
  const canEdit = role === "management" || role === "super_admin";
  const cfg = useResource("/reports/email-config");
  const company = useResource("/settings/company");

  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (editing && company.data) setEmail(company.data.email ?? "");
  }, [editing, company.data]);

  if (cfg.isLoading || company.isLoading)
    return <LoadingState label="Loading email configuration…" />;
  if (cfg.isError)
    return <ErrorState message={apiErrorMessage(cfg.error)} onRetry={cfg.refetch} />;

  const config = cfg.data || {};
  const recipients = Array.isArray(config.recipients) ? config.recipients : [];

  const save = async () => {
    setConfirm(false); setSaving(true);
    try {
      await api.put("/settings/company", { email: email.trim() });
      toast.success("Reports sender email updated");
      setEditing(false);
      company.refetch();
      cfg.refetch();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not update email"));
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      await api.post("/reports/email", {
        reportType: "Email Configuration Test",
        subject: "Freshezy ERP — email configuration test",
        filename: "email-test.pdf",
        title: "Email Configuration Test",
        subtitle: new Date().toLocaleString(),
        html: "<h2>Email configuration test</h2><p>This confirms report and production-requirement emails will reach the configured recipients.</p>",
      });
      toast.success(`Test email sent to ${recipients.length} recipient(s)`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Test email failed"));
    } finally { setTesting(false); }
  };

  return (
    <div className="space-y-5" data-testid="email-recipients">
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-stone-200 bg-gradient-to-br from-white to-amber-50/40 p-5 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-700 text-white"><Mail className="h-5 w-5" /></div>
          <div>
            <div className="font-display text-lg font-bold text-stone-900">Reports & Email Recipients</div>
            <div className="text-sm text-stone-600">Who receives production requirements, sales reports and other emailed documents.</div>
          </div>
        </div>
        <span
          data-testid="email-config-status"
          className={`rounded-full px-3 py-1 text-xs font-semibold ${config.configured ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}
        >
          {config.configured ? "Sending enabled" : "Not configured"}
        </span>
        {config.configured && config.domainVerified !== null && config.domainVerified !== undefined && (
          <span
            data-testid="email-domain-status"
            className={`rounded-full px-3 py-1 text-xs font-semibold ${config.domainVerified ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}
          >
            {config.domainVerified ? `Domain verified (${config.domain})` : `Domain not verified (${config.domain})`}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Sender / reply-to */}
        <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-display text-base font-semibold text-stone-900">Sender & Reply-To</h3>
            {canEdit && !editing && (
              <Button data-testid="email-edit-btn" size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => setEditing(true)}>
                <Pencil className="h-3.5 w-3.5" /> Edit
              </Button>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3 border-b border-stone-100 py-2.5">
            <div className="col-span-1 text-xs font-medium uppercase tracking-wide text-stone-500">From Name</div>
            <div className="col-span-2 text-sm text-stone-800" data-testid="email-from-name">{config.fromName || <span className="text-muted-foreground">—</span>}</div>
          </div>

          <div className="grid grid-cols-3 gap-3 border-b border-stone-100 py-2.5">
            <div className="col-span-1 text-xs font-medium uppercase tracking-wide text-stone-500">From Email</div>
            <div className="col-span-2 text-sm text-stone-800" data-testid="email-from-email">{config.senderEmail || <span className="text-muted-foreground">—</span>}</div>
          </div>

          <div className="grid grid-cols-3 items-center gap-3 py-2.5">
            <div className="col-span-1 text-xs font-medium uppercase tracking-wide text-stone-500">Reply-To</div>
            {editing ? (
              <div className="col-span-2 space-y-2">
                <Input data-testid="email-replyto-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="reports@company.com" className="h-9 text-sm" />
                <div className="flex gap-2">
                  <Button data-testid="email-save-btn" size="sm" className="h-8 gap-1.5 bg-emerald-600 hover:bg-emerald-700" disabled={saving || !email.trim()} onClick={() => setConfirm(true)}>
                    {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
                  </Button>
                  <Button data-testid="email-cancel-btn" size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => setEditing(false)}>
                    <X className="h-3.5 w-3.5" /> Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="col-span-2 text-sm text-stone-800" data-testid="email-replyto">{config.replyTo || <span className="text-muted-foreground">—</span>}</div>
            )}
          </div>

          <p className="mt-2 text-[11px] leading-relaxed text-stone-400">
            Reply-To is the Company Master email. Updating it changes the reply address and the primary recipient on every emailed report across web and mobile.
          </p>
        </div>

        {/* Recipients */}
        <div className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-display text-base font-semibold text-stone-900">Current Recipients</h3>
            <Button data-testid="email-send-test" size="sm" variant="outline" className="h-8 gap-1.5" disabled={testing || recipients.length === 0} onClick={sendTest}>
              {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send test
            </Button>
          </div>

          {recipients.length === 0 ? (
            <div className="rounded-lg border border-dashed border-stone-300 bg-stone-50 px-4 py-6 text-center text-sm text-muted-foreground">No recipients configured.</div>
          ) : (
            <ul className="space-y-2" data-testid="email-recipient-list">
              {recipients.map((r) => (
                <li key={r} data-testid={`email-recipient-${r}`} className="flex items-center gap-2 rounded-lg border border-stone-200 bg-stone-50/60 px-3 py-2 text-sm text-stone-800">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" /> {r}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-4 flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2.5 text-xs text-sky-800">
            <Users className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Recipients are the Company email plus active <strong>Management / Accounts</strong> users. Add or remove people under <strong>Users &amp; Roles</strong>; changes reflect here automatically.</span>
          </div>
        </div>
      </div>

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-amber-600" /> Update reports email</AlertDialogTitle>
            <AlertDialogDescription>
              This updates the shared Company Master email to <strong>{email.trim()}</strong>. It becomes the reply-to and primary recipient for all emailed reports on web and mobile. Proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="email-save-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="email-save-confirm" className="bg-emerald-600 hover:bg-emerald-700" onClick={save}>Yes, update</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
